import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { isConflict } from "../../http/errors";
import { useFineAttachmentsApi } from "../../http/fineAttachments/api";
import { FineAttachment } from "../../http/fineAttachments/types";
import { useAdminFinesApi } from "../../http/fines/api";
import { FineNoCarDependency } from "../../http/fines/types";
import { getLanguages, Language } from "../../http/subsidiaries/types";
import { useUsersApi } from "../../http/users/api";
import { User } from "../../http/users/types";
import { formatDateNumeric, formatTimeStamp } from "../../types/utils";
import SvgAdd from "../icons/Add";
import { InfoBox } from "../ui/Boxes";
import Button from "../ui/Button";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import ModalDialog from "../ui/ModalDialog";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Table from "../ui/Table";
import { FileToOverwrite, type TableUploadFileResult } from "../ui/TableFileUploadDialog";
import styles from "./NotifyFineDialog.module.css";

const USER_FIELDS = ["id", "fullName", "email", "areNotificationsDisabled"] as const;
type UserField = (typeof USER_FIELDS)[number];
type UserSearch = Pick<User, UserField>;

export type NotifyFineDialogFine = Pick<
  FineNoCarDependency,
  "id" | "driver" | "subsidiary" | "accountant" | "hrReferent"
>;

type NotifyFineFormBody = {
  accountant?: UserSearch | null;
  hrReferent?: UserSearch | null;
  language?: Language | null;
};

type NotifyFineDialogProps = {
  fine: NotifyFineDialogFine;
  onClose: () => void;
  onSent: () => void;
};

const NotifyFineDialog: FC<NotifyFineDialogProps> = ({ fine, onClose, onSent }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const finesApi = useAdminFinesApi();
  const fineAttachmentsApi = useFineAttachmentsApi();
  const usersApi = useUsersApi();
  const userSettings = useUserSettings();
  const aborter = useRef(new AbortController());
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<string[]>([]);
  const [fileToOverwrite, setFileToOverwrite] = useState<FileToOverwrite>();
  const [isOverwritingFile, setIsOverwritingFile] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canUploadDocuments = userSettings.permissions.fines.insert;

  const languages = getLanguages(i18n.t);

  // the notifications are sorted by date desc: the first one is the latest
  const notificationsResult = finesApi.useFineNotifications({ page: 1, limit: 1 }, aborter.current.signal, fine.id);
  const latestNotification = notificationsResult.data?.items[0];

  // the attachments are chosen among the documents saved in the fine, new ones can be uploaded here too
  const attachmentsResult = fineAttachmentsApi.useFineAttachments(
    { page: 1, limit: 100 },
    aborter.current.signal,
    fine.id,
  );
  const [attachments] = useDataInitializer(attachmentsResult.data, attachmentsResult.error);

  // the same rules are checked by the server before sending the email
  let driverError: string | undefined;
  if (fine.driver?.areNotificationsDisabled) {
    driverError = i18n.t.fines.notifyDialog.driverNotificationsDisabled(i18n.t.common.disableNotifications);
  } else if (!fine.driver?.email) {
    driverError = i18n.t.fines.notifyDialog.driverNoEmail;
  }

  const areAllAttachmentsSelected =
    !!attachments?.items.length && attachments.items.every((x) => selectedAttachmentIds.includes(x.id));

  const validateCcUser = (user?: UserSearch | null) => {
    if (user?.areNotificationsDisabled) {
      return i18n.t.fines.notifyDialog.userNotificationsDisabled(i18n.t.common.disableNotifications);
    }
    if (user && !user.email) {
      return i18n.t.fines.notifyDialog.userNoEmail;
    }
    return undefined;
  };

  // names of the documents uploaded in the dialog: they are selected to be sent as soon as the documents list is reloaded
  // (names are unique in a fine: a document with the same name is refused or overwritten)
  const [uploadedNames, setUploadedNames] = useState<string[]>([]);

  useEffect(() => {
    const uploaded = attachments?.items.filter((x) => uploadedNames.includes(x.name)) ?? [];
    if (!uploaded.length) {
      return;
    }

    setSelectedAttachmentIds((prev) => [...prev, ...uploaded.map((x) => x.id).filter((id) => !prev.includes(id))]);
    setUploadedNames((prev) => prev.filter((name) => !uploaded.some((x) => x.name === name)));
  }, [attachments, uploadedNames]);

  // same behaviour of the fine documents tab: a document with the same name can be overwritten
  const uploadAttachment = async (file: File): Promise<TableUploadFileResult> => {
    try {
      await fineAttachmentsApi.insertFineAttachment(file, false, fine.id);
      setUploadedNames((prev) => [...prev, file.name]);
    } catch (error) {
      if (!isConflict(error)) {
        throw error;
      }

      return new Promise<TableUploadFileResult>((resolve, reject) => {
        setFileToOverwrite({
          file,
          onCancel: () => {
            setFileToOverwrite(undefined);
            resolve("skipped");
          },
          onOverwrite: () => {
            setIsOverwritingFile(true);
            fineAttachmentsApi
              .insertFineAttachment(file, true, fine.id)
              .then(() => {
                setUploadedNames((prev) => [...prev, file.name]);
                setFileToOverwrite(undefined);
                resolve("success");
              })
              .catch((error) => {
                setFileToOverwrite(undefined);
                reject(error);
              })
              .finally(() => setIsOverwritingFile(false));
          },
        });
      });
    }
  };

  const uploadSelectedFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // allows to select again the same file
    event.target.value = "";

    setIsUploadingFiles(true);
    try {
      for (const file of files) {
        await uploadAttachment(file);
      }
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const onSubmit = async (formData: NotifyFineFormBody) => {
    // at least one document must be sent: the error is shown in the attachments field
    if (!selectedAttachmentIds.length) {
      return;
    }

    try {
      await finesApi.notifyDriver(fine.id, {
        language: formData.language!.value,
        attachmentIds: selectedAttachmentIds,
        accountantId: formData.accountant?.id ?? null,
        hrReferentId: formData.hrReferent?.id ?? null,
      });
      dispatch(showSuccessNotification(i18n.t));
      onSent();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.fines.notifyDriver}>
      <Form<NotifyFineFormBody>
        submitText={i18n.t.common.send}
        onSubmit={() => onSubmit}
        disabled={!!driverError}
        defaultValues={{
          accountant: fine.accountant,
          hrReferent: fine.hrReferent,
          // same default as the agreements: the subsidiary default language
          language: languages.find((x) => x.value == fine.subsidiary.defaultLanguage),
        }}
        secondaryButton={
          <Button type="secondary" buttonProps={{ type: "button", onClick: onClose }}>
            {i18n.t.common.cancel}
          </Button>
        }
      >
        {({ control, disabled, formState }) => {
          const isDisabled = disabled || formState.isSubmitting;
          const showAttachmentsError = formState.isSubmitted && !selectedAttachmentIds.length;

          return (
            <FormFieldsContainer>
              {latestNotification && (
                <InfoBox
                  text={i18n.t.fines.notifyDialog.alreadyNotified(
                    latestNotification.sentBy.fullName,
                    formatTimeStamp(latestNotification.sentAt, i18n.language),
                  )}
                />
              )}

              <FormField
                label={i18n.t.fines.notifyDialog.recipient}
                isOptional
                error={driverError ? { type: "validate", message: driverError } : undefined}
              >
                <Input
                  disabled
                  value={fine.driver?.email ? `${fine.driver.fullName} (${fine.driver.email})` : fine.driver?.fullName}
                />
              </FormField>

              {/* Accountant, in cc */}
              <Controller
                name="accountant"
                control={control}
                rules={{ validate: validateCcUser }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.fines.accountant} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<UserSearch, UserField>(
                        aborter,
                        searchTerm,
                        usersApi.getAdminUsers,
                        {
                          fields: USER_FIELDS,
                        },
                      );

                      return (
                        <AsyncSelect
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) => x.fullName}
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<UserSearch>(isInvalid)}
                          menuPosition="fixed"
                          isClearable
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              {/* HR referent, in cc */}
              <Controller
                name="hrReferent"
                control={control}
                rules={{ validate: validateCcUser }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.fines.hrReferent} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<UserSearch, UserField>(
                        aborter,
                        searchTerm,
                        usersApi.getAdminUsers,
                        {
                          fields: USER_FIELDS,
                        },
                      );

                      return (
                        <AsyncSelect
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) => x.fullName}
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<UserSearch>(isInvalid)}
                          menuPosition="fixed"
                          isClearable
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              {/* Language */}
              <Controller
                name="language"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.language} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select<Language>
                        {...field}
                        inputId={labelId}
                        options={languages}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        isDisabled={isDisabled}
                        isClearable
                        styles={reactSelectStyles<Language>(isInvalid)}
                        menuPosition="fixed"
                      />
                    )}
                  </FormField>
                )}
              />

              <FormField
                label={i18n.t.fines.notifyDialog.attachments}
                error={
                  showAttachmentsError
                    ? { type: "required", message: i18n.t.fines.notifyDialog.noAttachments }
                    : undefined
                }
                fullWidth
                labelAction={
                  canUploadDocuments && (
                    <>
                      <Button
                        type="secondary"
                        glyph={SvgAdd}
                        buttonProps={{
                          title: i18n.t.fines.notifyDialog.dropDocumentsHint,
                          disabled: isDisabled || isUploadingFiles,
                          onClick: () => fileInputRef.current?.click(),
                        }}
                      >
                        {i18n.t.fines.notifyDialog.addDocuments}
                      </Button>
                      <input ref={fileInputRef} type="file" multiple hidden onChange={uploadSelectedFiles} />
                    </>
                  )
                }
              >
                <Table<FineAttachment>
                  hoverTr={false}
                  fitWidth
                  fileDrop={{
                    enabled: canUploadDocuments && !isDisabled,
                    onUploadFile: uploadAttachment,
                  }}
                  head={
                    <>
                      <th align="left">{i18n.t.common.name}</th>
                      <th align="left">{i18n.t.common.uploadedBy}</th>
                      <th align="left">{i18n.t.common.uploadedAt}</th>
                    </>
                  }
                  renderRow={(item) => (
                    <>
                      <td>{item.name}</td>
                      <td>{item.uploader.fullName}</td>
                      <Table.Td noWrap>{formatDateNumeric(item.uploadedAt, i18n.language)}</Table.Td>
                    </>
                  )}
                  selectable={{
                    onRowSelect: (item, selected) =>
                      setSelectedAttachmentIds((prev) =>
                        selected ? [...prev, item.id] : prev.filter((id) => id !== item.id),
                      ),
                    isRowSelected: (item) => selectedAttachmentIds.includes(item.id),
                    isRowSelectable: () => !isDisabled,
                    selectAll: {
                      selected: areAllAttachmentsSelected,
                      toggle: () =>
                        setSelectedAttachmentIds(
                          areAllAttachmentsSelected ? [] : (attachments?.items ?? []).map((x) => x.id),
                        ),
                    },
                  }}
                  error={attachmentsResult.error}
                  items={attachments?.items}
                  total={attachments && !attachments.items.length ? 0 : undefined}
                />
                <p className={styles["attachments-hint"]}>{i18n.t.fines.notifyDialog.attachmentsHint}</p>
              </FormField>
            </FormFieldsContainer>
          );
        }}
      </Form>

      {!!fileToOverwrite && (
        <ModalDialog
          title={i18n.t.documents.alreadyPresent}
          isSubmitting={isOverwritingFile}
          onClose={() => {
            if (!isOverwritingFile) {
              fileToOverwrite.onCancel();
            }
          }}
          actions={[
            {
              title: i18n.t.common.cancel,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onCancel,
              type: "secondary",
            },
            {
              title: i18n.t.common.overwrite,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onOverwrite,
            },
          ]}
        >
          <p>{fileToOverwrite.file.name}</p>
          {i18n.t.documents.questions.overwrite}
        </ModalDialog>
      )}
    </ModalDialog>
  );
};

export default NotifyFineDialog;
